import React, {useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {ClickAwayListener, Tooltip} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// AD-2722: plain text with no border or shadow, the same style as the Create Report box pop-ups (Maria,
// 15 October 2026), on a white background so it can be read over the table.
const PLAIN_TOOLTIP = {
    tooltip: {
        sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            fontSize: 14,
            fontWeight: 400,
            border: 'none',
            boxShadow: 'none',
            px: 1.5,
            py: 1,
            maxWidth: 480,
            textWrap: 'balance'
        }
    }
};

// The icons open their pop-up on hover; a tap on an icon does what the icon does.
export const ContactTooltip = ({title, children}) => (
    <Tooltip title={title} componentsProps={PLAIN_TOOLTIP}>
        {children}
    </Tooltip>
);

ContactTooltip.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.element.isRequired
};

// Text that only explains something opens on hover and on a tap or click, so it can be read on a phone or tablet,
// and closes on a tap anywhere else.
export const TapTooltip = ({title, children}) => {
    const [open, setOpen] = useState(false);
    // A phone reports the finger leaving straight after a tap, so a tapped pop-up stays open until the next tap
    // elsewhere; a hovered one closes when the pointer leaves.
    const tapped = useRef(false);
    const close = () => {
        tapped.current = false;
        setOpen(false);
    };

    return (
        <ClickAwayListener onClickAway={close}>
            <Tooltip
                title={title}
                componentsProps={PLAIN_TOOLTIP}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => !tapped.current && setOpen(false)}
                disableTouchListener
            >
                {React.cloneElement(children, {
                    onClick: (event) => {
                        // inside a tick box label, a tap on the icon must not tick the box
                        event.preventDefault();
                        event.stopPropagation();
                        tapped.current = true;
                        setOpen(true);
                    }
                })}
            </Tooltip>
        </ClickAwayListener>
    );
};

TapTooltip.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.element.isRequired
};

// A heading or a label with an information icon that shows its pop-up.
// Maria, 19 October 2026: when the table needs the room, the icon goes under the heading and a long heading wraps
// AD-2740: 'inline' wraps a long question like ordinary text, left-aligned, with the icon kept after its last word
const ContactHelpTip = ({label, text, stacked, wrap, inline}) => {
    const icon = (
        <TapTooltip title={text}>
            <InfoOutlinedIcon
                fontSize="small"
                data-testid="contact-help-tip"
                aria-label={text}
                sx={{color: 'text.secondary', ml: stacked ? 0 : 0.5, mt: stacked ? 0.25 : 0, cursor: 'help', ...(inline && {verticalAlign: 'text-bottom'})}}
            />
        </TapTooltip>
    );
    if (inline && typeof label === 'string') {
        const lastSpace = label.trimEnd().lastIndexOf(' ');
        return (
            <span style={{whiteSpace: 'normal', overflowWrap: 'anywhere'}}>
                {label.slice(0, lastSpace + 1)}
                <span style={{whiteSpace: 'nowrap'}}>{label.slice(lastSpace + 1)}{icon}</span>
            </span>
        );
    }
    return (
        <span style={{display: 'inline-flex', alignItems: 'center', flexDirection: stacked ? 'column' : 'row', whiteSpace: wrap ? 'normal' : 'nowrap', textAlign: 'center'}}>
            {label}
            {icon}
        </span>
    );
};

ContactHelpTip.propTypes = {
    label: PropTypes.node,
    text: PropTypes.string.isRequired,
    stacked: PropTypes.bool,
    wrap: PropTypes.bool,
    inline: PropTypes.bool
};

export default ContactHelpTip;
